import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Checkbox } from '@vinnova/ui';
import {
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { FormQuestionFollowUp } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureOptions } from '../../../core/models/question/question-options.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-checkbox',
  templateUrl: './question-checkbox.component.html',
  styleUrls: ['./question-checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionCheckboxComponent implements OnInit, OnChanges {
  value: FormControl<string>;
  checkboxObjects: WritableSignal<Checkbox[]> = signal([]);
  questionChange = new Subject<void>();
  questionChange$ = this.questionChange.asObservable();
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  readonlyItems: WritableSignal<string[]> = signal([]);
  haveBeenUnchecked: boolean;
  helpText$: Observable<string>;
  questionNumber: string;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  labelPrefixForCheckbox = signal(undefined);
  showChanges = input<boolean>();

  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  @Input() repeaterGroupId: string;
  @Input() labelPrefix: string;

  _questionForm: FormGroup<ControlsOf<FormQuestionFollowUp>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionFollowUp>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  _question: Question;
  @Input()
  set question(value: Question) {
    this._question = value;
    this.questionChange.next();
    this.helpText$ = this.createHelpText();
  }

  get question() {
    return this._question;
  }

  get structure() {
    return this.question.structure as QuestionStructureOptions;
  }

  get minNumberOfCheckedOptions() {
    return this.structure.minNumberOfCheckedOptions;
  }

  get maxNumberOfCheckedOptions() {
    return this.structure.maxNumberOfCheckedOptions;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private questionNumberService: QuestionNumberService,
    private translocoService: TranslocoService,
    private translatePipe: TranslatePipe,
    private formValidationService: FormValidationService,
    private formsQuery: RendererFormsQuery,
  ) {}

  readonly previousAnswersService = inject(PreviousAnswersService);

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
    const validatorList = [];
    if (this.maxNumberOfCheckedOptions && this.minNumberOfCheckedOptions) {
      validatorList.push(
        this.validateMaxCheckedValues(),
        this.validateMinCheckedValues(),
      );
    } else if (this.maxNumberOfCheckedOptions) {
      validatorList.push(this.validateMaxCheckedValues());
    } else if (this.minNumberOfCheckedOptions) {
      validatorList.push(this.validateMinCheckedValues());
    }

    this.questionForm.get('value').setValidators(validatorList);

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    const updateTextObs = merge(
      this.translocoService.langChanges$,
      this.questionChange$,
    );

    updateTextObs
      .pipe(
        map(() => {
          return (
            this.question.structure as QuestionStructureOptions
          ).options.map((option) => {
            return {
              id: option.value,
              label: this.translatePipe.transform(
                this.question.translations?.list[option.value],
                this.translocoService.getActiveLang(),
                option.text,
              ),
            };
          });
        }),
        untilDestroyed(this),
      )
      .subscribe((items) => {
        this.checkboxObjects.set(items);
        this.setReadonly();
      });

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    this.readOnly$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setReadonly());

    this.errorMessage$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return this.formValidationService
              .questionInvalid(this.value, this.question.id)
              .pipe(
                switchMap((invalid) => {
                  return invalid
                    ? this.createHelpText(this.checkboxObjects().length === 1)
                    : of(undefined);
                }),
              );
          }
          case ReadonlyState.Locked: {
            return this.value.invalid ? this.createHelpText() : of(undefined);
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );

    this.labelPrefixForCheckbox.set(
      this.labelPrefix
        ? this.labelPrefix + this.question.id.split('-')[0] + '-'
        : this.question.id.split('-')[0] + '-',
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonly();
      }
    }
  }

  createHelpText(errorSingleCheckbox?: boolean): Observable<string> {
    if (this.maxNumberCheck() && this.minNumberCheck()) {
      return this.translocoService.selectTranslate(
        'checkboxes_min_and_max_NumberOfCheckedOptions',
        {
          minNumberOfCheckedOptions: this.minNumberOfCheckedOptions,
          maxNumberOfCheckedOptions: this.maxNumberOfCheckedOptions,
        },
      );
    } else if (this.minNumberCheck()) {
      return errorSingleCheckbox
        ? this.translocoService.selectTranslate('checkbox_required')
        : this.structure.options.length > 1
          ? this.translocoService.selectTranslate('checkbox_min', {
              minVal: this.minNumberOfCheckedOptions,
            })
          : of(undefined);
    } else if (this.maxNumberCheck()) {
      return this.maxNumberOfCheckedOptions > 1
        ? this.translocoService.selectTranslate('checkbox_max', {
            maxVal: this.maxNumberOfCheckedOptions,
          })
        : this.translocoService.selectTranslate(
            'checkboxes_maxNumberOfCheckedOptions_is_one',
          );
    } else {
      return of(undefined);
    }
  }

  maxNumberCheck() {
    return (
      this.maxNumberOfCheckedOptions !== null &&
      !isNaN(this.maxNumberOfCheckedOptions)
    );
  }

  minNumberCheck() {
    return (
      this.minNumberOfCheckedOptions !== null &&
      !isNaN(this.minNumberOfCheckedOptions)
    );
  }

  validateMinCheckedValues(): ValidatorFn {
    return (control: AbstractControl) => {
      const checkedValues = control.value
        ? JSON.parse(control.value).length
        : 0;
      if (checkedValues < this.minNumberOfCheckedOptions) {
        return { minlength: true };
      }
    };
  }

  validateMaxCheckedValues(): ValidatorFn {
    return (control: AbstractControl) => {
      const checkedValues = control.value
        ? JSON.parse(control.value).length
        : 0;
      if (checkedValues > this.maxNumberOfCheckedOptions) {
        return { maxlength: true };
      }
    };
  }

  private getChoosenCheckbox(checkboxes: Checkbox[]): string[] {
    const choosenCheckbox = [];
    const selected = JSON.parse(this.value.value);
    for (const checkbox of checkboxes) {
      if (selected?.includes(checkbox.id)) {
        choosenCheckbox.push(checkbox.label);
      }
    }
    return choosenCheckbox;
  }

  private setReadonly() {
    if (this.formsQuery.getReadOnly()) {
      this.readonlyItems.set(this.getChoosenCheckbox(this.checkboxObjects()));
    }
  }
}
